/**
 * Utilidades para impresión térmica y generación de PDFs
 */
import { BRAND } from "@/config/branding";

// Tamaño base del logo en comandas térmicas de 80mm: 240px es ya ~92% del ancho
// físico imprimible real (80mm - márgenes @page - padding del body ≈ 260px).
// Se aplica BRAND.logoEscala pero se limita (clamp) a 260px para no arriesgar
// que la impresora térmica corte el logo en hoteles con escala alta.
const LOGO_COMANDA_BASE_PX = 240;
const LOGO_COMANDA_MAX_SEGURO_PX = 260;
const LOGO_COMANDA_PX = Math.min(
  Math.round(LOGO_COMANDA_BASE_PX * (BRAND.logoEscala || 1)),
  LOGO_COMANDA_MAX_SEGURO_PX
);

/**
 * Genera e imprime un recibo de pago de restaurante
 */
export function imprimirReciboRestaurante(pago, cuenta, detallesCuenta) {
  const width = 800;
  const height = 600;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;

  const win = window.open(
    "",
    "Imprimir",
    `width=${width},height=${height},left=${left},top=${top},toolbar=0,scrollbars=1,status=0,resizable=1`,
  );
  if (win) win.blur();

  const styles = `
    <style>
      body {
        font-family: monospace;
        font-size: 12px;
        margin: 0;
        padding: 10px;
        line-height: 1.4;
      }
      .titulo-hotel {
        text-align: center;
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 5px;
      }
      .subtitulo {
        text-align: center;
        font-size: 11px;
        margin-bottom: 10px;
        color: #000;
        font-weight: bold;
      }
      .linea {
        border-top: 1px dashed #000;
        margin: 8px 0;
      }
      .linea-doble {
        border-top: 2px solid #000;
        margin: 8px 0;
      }
      .fila {
        display: flex;
        justify-content: space-between;
        margin-bottom: 3px;
      }
      .total {
        font-weight: bold;
        font-size: 14px;
        margin-top: 5px;
      }
      .right { text-align: right; }
      .center { text-align: center; }
      .small {
        font-size: 10px;
        color: #000;
        font-weight: bold;
      }
      .info-pago {
        margin: 10px 0;
        padding: 8px 0;
        border-top: 1px solid #000;
        border-bottom: 1px solid #000;
      }
      .pie {
        text-align: center;
        margin-top: 15px;
        font-size: 11px;
      }
    </style>
  `;

  const fecha = new Date();
  const fechaFormateada = `${fecha.getDate().toString().padStart(2, "0")}/${(
    fecha.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${fecha.getFullYear()}`;
  const horaFormateada = `${fecha
    .getHours()
    .toString()
    .padStart(2, "0")}:${fecha.getMinutes().toString().padStart(2, "0")}`;

  let html = `<div class='titulo-hotel'>${BRAND.nombre}</div>`;
  html += `<div class='subtitulo'>RECIBO DE PAGO</div>`;
  html += `<div class='small center'>Fecha: ${fechaFormateada} ${horaFormateada}</div>`;
  html += `<div class='linea'></div>`;

  // Información de la cuenta
  if (cuenta) {
    html += `<div class='small'>Cuenta: ${cuenta.NombreCuenta || "N/A"}</div>`;
    if (cuenta.IdCuentaPedido) {
      html += `<div class='small'>No. Cuenta: ${cuenta.IdCuentaPedido}</div>`;
    }
  }

  html += `<div class='linea'></div>`;

  // Detalles de items (si están disponibles)
  if (detallesCuenta && detallesCuenta.length > 0) {
    html += `<div style='margin-bottom: 8px;'><strong>ITEMS CONSUMIDOS:</strong></div>`;
    detallesCuenta.forEach((det) => {
      const cantidad = det.Cantidad || det.cantidad || 1;
      html += `<div class='fila'>`;
      html += `<span>${det.nombre_platillo} x${cantidad}</span>`;
      html += `<span>Q${Number(det.Subtotal).toFixed(2)}</span>`;
      html += `</div>`;
    });
    html += `<div class='linea'></div>`;
  }

  // Totales
  const subtotal = Number(cuenta?.Subtotal || cuenta?.Total || 0);
  const propina = Number(cuenta?.Propina || 0);
  const total = Number(
    cuenta?.Total || cuenta?.TotalFinal || subtotal + propina,
  );

  html += `<div class='fila'><span>Subtotal</span><span>Q${subtotal.toFixed(
    2,
  )}</span></div>`;
  if (propina > 0) {
    html += `<div class='fila'><span>Propina</span><span>Q${propina.toFixed(
      2,
    )}</span></div>`;
  }
  html += `<div class='linea-doble'></div>`;
  html += `<div class='fila total'><span>TOTAL</span><span>Q${total.toFixed(
    2,
  )}</span></div>`;

  html += `<div class='linea'></div>`;

  // Información del pago
  html += `<div class='info-pago'>`;
  html += `<div><strong>PAGO RECIBIDO:</strong></div>`;
  html += `<div class='fila'><span>Monto pagado</span><span>Q${Number(
    pago.Monto,
  ).toFixed(2)}</span></div>`;
  html += `<div class='fila'><span>Método</span><span>${
    pago.metodo_pago || "N/A"
  }</span></div>`;

  if (pago.NumeroReferencia) {
    html += `<div class='fila'><span>Referencia</span><span>${pago.NumeroReferencia}</span></div>`;
  }

  if (pago.cuenta_bancaria) {
    html += `<div class='small'>${pago.cuenta_bancaria}</div>`;
  }

  const saldoPendiente = total - Number(pago.Monto);
  if (saldoPendiente > 0) {
    html += `<div class='fila' style='margin-top: 5px;'><span>Saldo pendiente</span><span>Q${saldoPendiente.toFixed(
      2,
    )}</span></div>`;
  } else {
    html += `<div class='center' style='margin-top: 5px;'><strong>✓ PAGADO EN SU TOTALIDAD</strong></div>`;
  }
  html += `</div>`;

  html += `<div class='pie'>¡Gracias por su preferencia!</div>`;
  html += `<div class='small center'>Este es un recibo válido de pago</div>`;

  win.document.write(
    `<html><head><title>Recibo de Pago</title>${styles}</head><body>${html}</body></html>`,
  );
  win.document.close();

  setTimeout(() => {
    win.print();
    win.close();
  }, 500);
}

/**
 * Genera e imprime un recibo de pago de hotel para facturas.comandas
 */
export function imprimirReciboHotel(pago, habitacion, reserva) {
  const width = 800;
  const height = 600;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;

  const win = window.open(
    "",
    "Imprimir",
    `width=${width},height=${height},left=${left},top=${top},toolbar=0,scrollbars=1,status=0,resizable=1`,
  );
  if (win) win.blur();

  win.onload = () => {
    win.print();
    win.close(); //Cerrar tras imprimir
  };

  const styles = `
    <style>
      body {
        font-family: monospace;
        font-size: 12px;
        margin: 0;
        padding: 10px;
        line-height: 1.4;
      }
      .titulo-hotel {
        text-align: center;
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 5px;
      }
      .subtitulo {
        text-align: center;
        font-size: 11px;
        margin-bottom: 10px;
        color: #000;
        font-weight: bold;
      }
      .linea {
        border-top: 1px dashed #000;
        margin: 8px 0;
      }
      .linea-doble {
        border-top: 2px solid #000;
        margin: 8px 0;
      }
      .fila {
        display: flex;
        justify-content: space-between;
        margin-bottom: 3px;
      }
      .total {
        font-weight: bold;
        font-size: 14px;
        margin-top: 5px;
      }
      .right { text-align: right; }
      .center { text-align: center; }
      .small {
        font-size: 10px;
        color: #000;
        font-weight: bold;
      }
      .info-pago {
        margin: 10px 0;
        padding: 8px 0;
        border-top: 1px solid #000;
        border-bottom: 1px solid #000;
      }
      .pie {
        text-align: center;
        margin-top: 15px;
        font-size: 11px;
      }
    </style>
  `;

  const fecha = new Date();
  const fechaFormateada = `${fecha.getDate().toString().padStart(2, "0")}/${(
    fecha.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${fecha.getFullYear()}`;
  const horaFormateada = `${fecha
    .getHours()
    .toString()
    .padStart(2, "0")}:${fecha.getMinutes().toString().padStart(2, "0")}`;

  let html = `<div class='titulo-hotel'>${BRAND.nombre}</div>`;
  html += `<div class='subtitulo'>RECIBO DE PAGO - HOSPEDAJE</div>`;
  html += `<div class='small center'>Fecha: ${fechaFormateada} ${horaFormateada}</div>`;
  html += `<div class='linea'></div>`;

  // Información de la reserva
  if (habitacion) {
    html += `<div class='fila'><span>Habitación:</span><span>${
      habitacion.Numero || "N/A"
    }</span></div>`;
  }

  if (reserva) {
    html += `<div class='fila'><span>Reserva:</span><span>${
      reserva.CodigoReserva || "N/A"
    }</span></div>`;
    if (reserva.huesped) {
      html += `<div class='small'>Huésped: ${reserva.huesped.Nombre || ""} ${
        reserva.huesped.Apellido || ""
      }</div>`;
    }
  }

  html += `<div class='linea'></div>`;

  // Información del pago
  html += `<div class='info-pago'>`;
  html += `<div><strong>PAGO RECIBIDO:</strong></div>`;
  html += `<div class='fila'><span>Monto pagado</span><span>Q${Number(
    pago.Monto,
  ).toFixed(2)}</span></div>`;
  html += `<div class='fila'><span>Método</span><span>${
    pago.metodo_pago || "N/A"
  }</span></div>`;

  if (pago.NumeroReferencia) {
    html += `<div class='fila'><span>Referencia</span><span>${pago.NumeroReferencia}</span></div>`;
  }

  if (pago.cuenta_bancaria) {
    html += `<div class='small'>${pago.cuenta_bancaria}</div>`;
  }

  // Manejar tanto SaldoPendiente como SaldoPendienteHabitacion
  const saldoAnterior =
    habitacion?.SaldoPendiente !== undefined
      ? habitacion.SaldoPendiente
      : habitacion?.SaldoPendienteHabitacion;

  if (habitacion && saldoAnterior !== undefined) {
    const nuevoSaldo = Number(saldoAnterior) - Number(pago.Monto);
    html += `<div class='linea'></div>`;
    html += `<div class='fila'><span>Saldo anterior</span><span>Q${Number(
      saldoAnterior,
    ).toFixed(2)}</span></div>`;
    html += `<div class='fila total'><span>Nuevo saldo</span><span>Q${nuevoSaldo.toFixed(
      2,
    )}</span></div>`;

    if (nuevoSaldo <= 0) {
      html += `<div class='center' style='margin-top: 5px;'><strong>✓ PAGADO EN SU TOTALIDAD</strong></div>`;
    }
  }
  html += `</div>`;

  html += `<div class='pie'>¡Gracias por su preferencia!</div>`;
  html += `<div class='small center'>Este es un recibo válido de pago</div>`;

  win.document.write(
    `<html><head><title>Recibo de Pago</title>${styles}</head><body>${html}</body></html>`,
  );
  win.document.close();

  setTimeout(() => {
    win.print();
    win.close();
  }, 500);
}

/**
 * Genera e imprime comanda para cocina
 */
export function imprimirComandaCocina(
  pedido,
  detalles,
  mesa,
  mesero = null,
  correlativo = null,
) {
  const width = 400;
  const height = 600;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;

  const win = window.open(
    "",
    "ImprimirComandaCocina",
    `width=${width},height=${height},left=${left},top=${top},toolbar=0,scrollbars=1,status=0,resizable=1`,
  );
  if (win) win.blur();

  if (!win) {
    console.warn(
      "Popup bloqueado. Por favor permite popups para imprimir comandas.",
    );
    return;
  }

  const styles = `
    <style>
      @page { size: 80mm auto; margin: 0 4mm 4mm; }
      body {
        font-family: monospace;
        font-size: 12px;
        margin: 0;
        padding: 4px 6px;
        line-height: 1.25;
      }
      .titulo {
        text-align: center;
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 2px;
        text-transform: uppercase;
      }
      .logo-comanda {
        text-align: center;
        margin-bottom: 4px;
      }
      .logo-comanda img {
        max-width: ${LOGO_COMANDA_PX}px;
        height: auto;
        display: block;
        margin: 0 auto;
        filter: brightness(0);
      }
      .num-comanda {
        text-align: center;
        font-size: 22px;
        font-weight: bold;
        margin: 3px 0;
        letter-spacing: 2px;
      }
      .subtitulo {
        text-align: center;
        font-size: 11px;
        font-weight: bold;
        color: #000;
        margin-bottom: 4px;
      }
      .linea {
        border-top: 2px solid #000;
        margin: 5px 0;
      }
      .linea-fina {
        border-top: 1px dashed #000;
        margin: 3px 0;
      }
      .mesa-info {
        text-align: center;
        font-size: 18px;
        font-weight: bold;
        padding: 5px 0;
        color: #000;
        margin: 4px 0;
      }
      .item {
        margin-bottom: 6px;
        padding: 4px 0;
        border-bottom: 1px solid #000;
        overflow: hidden;
      }
      .item-cantidad {
        font-size: 20px;
        font-weight: bold;
        float: left;
        margin-right: 8px;
      }
      .item-nombre {
        font-size: 13px;
        font-weight: bold;
        display: block;
      }
      .item-detalle {
        font-size: 11px;
        color: #000;
        font-weight: bold;
        margin-left: 30px;
        margin-top: 2px;
      }
      .item-guarnicion {
        font-size: 12px;
        font-weight: bold;
        margin-left: 30px;
        margin-top: 2px;
      }
      .item-nota {
        font-size: 10px;
        font-style: italic;
        margin-left: 30px;
        margin-top: 2px;
        font-weight: bold;
      }
      .cuenta-tag {
        display: inline-block;
        background: #000;
        color: white;
        padding: 1px 5px;
        border-radius: 3px;
        font-size: 9px;
        margin-left: 6px;
      }
      .info-pedido {
        font-size: 10px;
        color: #000;
        font-weight: bold;
        text-align: center;
        margin-top: 5px;
      }
      .hora {
        text-align: center;
        font-size: 12px;
        font-weight: bold;
        margin: 2px 0;
      }
    </style>
  `;

  const fecha = new Date();
  const horaFormateada = `${fecha
    .getHours()
    .toString()
    .padStart(2, "0")}:${fecha.getMinutes().toString().padStart(2, "0")}`;

  const logoUrlCocina = window.location.origin + BRAND.logo;
  let html = `<div class='logo-comanda'><img src='${logoUrlCocina}' alt='${BRAND.nombre}'></div>`;
  html += `<div class='titulo'>COMANDA - COCINA</div>`;
  html += `<div class='hora'>${horaFormateada}</div>`;
  // Número de pedido — siempre visible y grande
  html += `<div class='num-comanda'>Pedido #${pedido?.IdPedido || "N/A"}</div>`;
  if (correlativo) {
    html += `<div class='subtitulo'>Comanda: ${correlativo}</div>`;
  }
  html += `<div class='mesa-info'>MESA ${mesa?.Numero || "N/A"}</div>`;
  html += `<div class='linea'></div>`;

  // Agrupar items por cuenta
  const itemsPorCuenta = {};
  detalles.forEach((det) => {
    const cuentaNombre = det.cuenta_nombre || "Sin cuenta";
    if (!itemsPorCuenta[cuentaNombre]) {
      itemsPorCuenta[cuentaNombre] = [];
    }
    itemsPorCuenta[cuentaNombre].push(det);
  });

  // Imprimir items agrupados por cuenta
  Object.keys(itemsPorCuenta).forEach((cuentaNombre) => {
    const items = itemsPorCuenta[cuentaNombre];

    items.forEach((det) => {
      html += `<div class='item'>`;
      html += `<span class='item-cantidad'>${det.cantidad || 1}x</span>`;
      html += `<span class='item-nombre'>${det.nombre_platillo}`;
      html += `<span class='cuenta-tag'>${cuentaNombre}</span>`;
      html += `</span>`;

      // Guarniciones (sin costo) y extras (con costo)
      if (det.opciones && det.opciones.length > 0) {
        det.opciones.forEach((opcion) => {
          const precioExtra = Number(opcion.precio_extra || 0);
          const cantidad = Number(opcion.cantidad || 1);
          if (precioExtra > 0) {
            const cantStr = cantidad > 1 ? ` x${cantidad}` : "";
            html += `<div class='item-detalle'>EXTRA: ${
              opcion.nombre
            }${cantStr} (+Q${precioExtra.toFixed(2)})</div>`;
          } else {
            html += `<div class='item-guarnicion'>\u2192 ${opcion.nombre}</div>`;
          }
        });
      }

      // Ingredientes que no quiere
      if (det.ingredientes_no_quiere && det.ingredientes_no_quiere.length > 0) {
        det.ingredientes_no_quiere.forEach((ing) => {
          html += `<div class='item-nota'>SIN ${ing.nombre}</div>`;
        });
      }

      // Notas especiales
      if (det.notas) {
        html += `<div class='item-nota'>NOTA: ${det.notas}</div>`;
      }

      html += `</div>`;
    });
  });

  html += `<div class='linea'></div>`;
  html += `<div class='info-pedido'>`;
  html += `Pedido #${pedido?.IdPedido || "N/A"}<br>`;
  if (mesero) {
    html += `<strong>Mesero: ${mesero}</strong><br>`;
  }
  html += `</div>`;
  html += `<div class='linea-fina' style='margin-top:50px;'></div>`;
  html += `<div style='margin-top:20px;'>&nbsp;</div>`;

  win.document.write(
    `<html><head><title>Comanda Cocina</title>${styles}</head><body>${html}</body></html>`,
  );
  win.document.close();

  const imgCocina = win.document.querySelector('img');
  if (imgCocina && !imgCocina.complete) {
    imgCocina.onload  = () => { win.focus(); win.print(); win.close(); };
    imgCocina.onerror = () => { win.focus(); win.print(); win.close(); };
  } else {
    setTimeout(() => { win.focus(); win.print(); win.close(); }, 300);
  }
}

/**
 * Genera e imprime comanda para barra
 */
export function imprimirComandaBarra(
  pedido,
  detalles,
  mesa,
  mesero = null,
  correlativo = null,
) {
  // Similar a cocina pero filtrado por items de barra
  const detallesBarra = detalles.filter((d) => d.area_produccion === "Barra");

  if (detallesBarra.length === 0) {
    return; // No hay items de barra
  }

  const width = 400;
  const height = 600;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;

  const win = window.open(
    "",
    "ImprimirComandaBarra",
    `width=${width},height=${height},left=${left},top=${top},toolbar=0,scrollbars=1,status=0,resizable=1`,
  );
  if (win) win.blur();

  if (!win) {
    console.warn(
      "Popup bloqueado. Por favor permite popups para imprimir comandas.",
    );
    return;
  }

  const styles = `
    <style>
      @page { size: 80mm auto; margin: 0 4mm 4mm; }
      body {
        font-family: monospace;
        font-size: 13px;
        margin: 0;
        padding: 4px 6px;
        line-height: 1.3;
      }
      .titulo {
        text-align: center;
        font-size: 18px;
        font-weight: bold;
        margin-bottom: 5px;
        text-transform: uppercase;
        color: #000;
      }
      .num-comanda {
        text-align: center;
        font-size: 22px;
        font-weight: bold;
        margin: 3px 0;
        letter-spacing: 2px;
      }
      .subtitulo {
        text-align: center;
        font-size: 12px;
        font-weight: bold;
        color: #000;
        margin-bottom: 10px;
      }
      .linea {
        border-top: 2px solid #000;
        margin: 10px 0;
      }
      .mesa-info {
        text-align: center;
        font-size: 20px;
        font-weight: bold;
        padding: 10px 0;
        color: #000;
        margin: 10px 0;
      }
      .item {
        margin-bottom: 12px;
        padding: 8px 0;
        border-bottom: 1px solid #000;
      }
      .item-cantidad {
        font-size: 24px;
        font-weight: bold;
        float: left;
        margin-right: 10px;
      }
      .item-nombre {
        font-size: 14px;
        font-weight: bold;
        display: block;
      }
      .item-detalle {
        font-size: 11px;
        color: #000;
        font-weight: bold;
        margin-left: 35px;
        margin-top: 3px;
      }
      .cuenta-tag {
        display: inline-block;
        background: #000;
        color: white;
        padding: 3px 8px;
        border-radius: 3px;
        font-size: 10px;
        margin-left: 10px;
      }
      .info-pedido {
        font-size: 11px;
        color: #000;
        font-weight: bold;
        text-align: center;
        margin-top: 10px;
      }
      .hora {
        text-align: center;
        font-size: 14px;
        font-weight: bold;
        margin: 5px 0;
      }
    </style>
  `;

  const fecha = new Date();
  const horaFormateada = `${fecha
    .getHours()
    .toString()
    .padStart(2, "0")}:${fecha.getMinutes().toString().padStart(2, "0")}`;

  const logoUrlBarra = window.location.origin + BRAND.logo;
  let html = `<div style='text-align:center;margin-bottom:4px;'><img src='${logoUrlBarra}' style='max-width:${LOGO_COMANDA_PX}px;height:auto;display:block;margin:0 auto;filter:brightness(0);' alt='${BRAND.nombre}'></div>`;
  html += `<div class='titulo'>COMANDA - BARRA</div>`;
  html += `<div class='hora'>${horaFormateada}</div>`;
  html += `<div class='num-comanda'>Pedido #${pedido?.IdPedido || "N/A"}</div>`;
  if (correlativo) {
    html += `<div class='subtitulo'>Comanda: ${correlativo}</div>`;
  }
  html += `<div class='mesa-info'>MESA ${mesa?.Numero || "N/A"}</div>`;
  html += `<div class='linea'></div>`;

  detallesBarra.forEach((det) => {
    html += `<div class='item'>`;
    html += `<span class='item-cantidad'>${det.cantidad || 1}x</span>`;
    html += `<span class='item-nombre'>${det.nombre_platillo}`;
    if (det.cuenta_nombre) {
      html += `<span class='cuenta-tag'>${det.cuenta_nombre}</span>`;
    }
    html += `</span>`;

    if (det.opciones && det.opciones.length > 0) {
      det.opciones.forEach((opcion) => {
        const precioExtra = Number(opcion.precio_extra || 0);
        const precioStr =
          precioExtra > 0 ? ` (+Q${precioExtra.toFixed(2)})` : "";
        html += `<div class='item-detalle'>+ ${opcion.nombre}${precioStr}</div>`;
      });
    }

    html += `</div>`;
  });

  html += `<div class='linea'></div>`;
  html += `<div class='info-pedido'>`;
  html += `Pedido #${pedido?.IdPedido || "N/A"}<br>`;
  if (mesero) {
    html += `<strong>Mesero: ${mesero}</strong><br>`;
  }
  html += `</div>`;
  html += `<div class='linea-fina' style='margin-top:50px;'></div>`;
  html += `<div style='margin-top:20px;'>&nbsp;</div>`;

  win.document.write(
    `<html><head><title>Comanda Barra</title>${styles}</head><body>${html}</body></html>`,
  );
  win.document.close();

  const imgBarra = win.document.querySelector('img');
  if (imgBarra && !imgBarra.complete) {
    imgBarra.onload  = () => { win.focus(); win.print(); win.close(); };
    imgBarra.onerror = () => { win.focus(); win.print(); win.close(); };
  } else {
    setTimeout(() => { win.focus(); win.print(); win.close(); }, 300);
  }
}

/**
 * Genera e imprime un recibo de venta de tienda
 */
export function imprimirReciboTienda(venta, detalles, pagos) {
  const win = window.open(
    "",
    "",
    "width=10,height=10,left=0,top=0,toolbar=0,scrollbars=0,status=0,resizable=0",
  );
  if (win) win.blur();

  const styles = `
    <style>
      body {
        font-family: monospace;
        font-size: 12px;
        margin: 0;
        padding: 10px;
        line-height: 1.4;
      }
      .titulo-hotel {
        text-align: center;
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 5px;
      }
      .subtitulo {
        text-align: center;
        font-size: 11px;
        margin-bottom: 10px;
        color: #000;
        font-weight: bold;
      }
      .linea {
        border-top: 1px dashed #000;
        margin: 8px 0;
      }
      .linea-doble {
        border-top: 2px solid #000;
        margin: 8px 0;
      }
      .fila {
        display: flex;
        justify-content: space-between;
        margin-bottom: 3px;
      }
      .total {
        font-weight: bold;
        font-size: 14px;
        margin-top: 5px;
      }
      .right { text-align: right; }
      .center { text-align: center; }
      .small {
        font-size: 10px;
        color: #000;
        font-weight: bold;
      }
      .info-pago {
        margin: 10px 0;
        padding: 8px 0;
        border-top: 1px solid #000;
        border-bottom: 1px solid #000;
      }
      .pie {
        text-align: center;
        margin-top: 15px;
        font-size: 11px;
      }
    </style>
  `;

  const fecha = new Date();
  const fechaFormateada = `${fecha.getDate().toString().padStart(2, "0")}/${
    fecha.getMonth() + 1
  }
    .toString()
    .padStart(2, "0")}/${fecha.getFullYear()}`;
  const horaFormateada = `${fecha
    .getHours()
    .toString()
    .padStart(2, "0")}:${fecha.getMinutes().toString().padStart(2, "0")}`;

  let html = `<div class='titulo-hotel'>${BRAND.nombre}</div>`;
  html += `<div class='subtitulo'>RECIBO DE VENTA - TIENDA</div>`;
  html += `<div class='small center'>Fecha: ${fechaFormateada} ${horaFormateada}</div>`;
  html += `<div class='linea'></div>`;

  // Detalles de productos
  if (detalles && detalles.length > 0) {
    html += `<div style='margin-bottom: 8px;'><strong>PRODUCTOS:</strong></div>`;
    detalles.forEach((det) => {
      const cantidad = det.cantidad || det.Cantidad || 1;
      const precio = Number(det.PrecioVenta || det.precio || 0);
      const subtotal = cantidad * precio;
      html += `<div class='fila'>`;
      html += `<span>${det.Nombre || det.nombre} x${cantidad}</span>`;
      html += `<span>Q${subtotal.toFixed(2)}</span>`;
      html += `</div>`;
    });
    html += `<div class='linea'></div>`;
  }

  // Total
  const total = Number(venta?.Total || 0);
  html += `<div class='linea-doble'></div>`;
  html += `<div class='fila total'><span>TOTAL</span><span>Q${total.toFixed(
    2,
  )}</span></div>`;
  html += `<div class='linea'></div>`;

  // Información de pagos
  if (pagos && pagos.length > 0) {
    html += `<div class='info-pago'>`;
    html += `<div><strong>MÉTODOS DE PAGO:</strong></div>`;
    pagos.forEach((pago) => {
      html += `<div class='fila'>`;
      html += `<span>${pago.Metodo || pago.metodo || "N/A"}</span>`;
      html += `<span>Q${Number(pago.Monto || pago.Cantidad || 0).toFixed(
        2,
      )}</span>`;
      html += `</div>`;
      if (pago.NumeroReferencia) {
        html += `<div class='small'>Ref: ${pago.NumeroReferencia}</div>`;
      }
    });
    html += `</div>`;
  }

  html += `<div class='pie'>`;
  html += `Gracias por su compra<br>`;
  html += `¡Vuelva pronto!`;
  html += `</div>`;

  win.document.write(
    `<html><head><title>Recibo Tienda</title>${styles}</head><body>${html}</body></html>`,
  );
  win.document.close();

  setTimeout(() => {
    win.print();
    win.close();
  }, 500);
}

/**
 * Genera e imprime la comanda de CAJA/CLIENTE
 * Incluye: número de comanda, items con precios, propina, total y campos para NIT/habitación/dirección
 * @param {object} pedido       - { IdPedido }
 * @param {object} cuenta       - { NombreCuenta, Subtotal }
 * @param {Array}  detalles     - [{ cantidad, nombre_platillo, precio_unitario, subtotal }]
 * @param {object} mesa         - { Numero }
 * @param {string|null} mesero
 * @param {string|null} correlativo - número de comanda (mismo que cocina)
 */
export function imprimirComandaCaja(
  pedido,
  cuenta,
  detalles,
  mesa,
  mesero = null,
  correlativo = null,
) {
  const width = 400;
  const height = 700;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;

  const win = window.open(
    "",
    "ImprimirComandaCaja",
    `width=${width},height=${height},left=${left},top=${top},toolbar=0,scrollbars=1,status=0,resizable=1`,
  );
  if (win) win.blur();

  if (!win) {
    console.warn("Popup bloqueado. Por favor permite popups para imprimir.");
    return;
  }

  const styles = `
    <style>
      @page { size: 80mm auto; margin: 0 4mm 4mm; }
      body {
        font-family: monospace;
        font-size: 12px;
        margin: 0;
        padding: 4px 6px;
        line-height: 1.3;
      }
      .titulo {
        text-align: center;
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 2px;
        text-transform: uppercase;
      }
      .hotel {
        text-align: center;
        font-size: 13px;
        font-weight: bold;
        margin-bottom: 2px;
      }
      .logo-comanda {
        text-align: center;
        margin-bottom: 4px;
      }
      .logo-comanda img {
        max-width: ${LOGO_COMANDA_PX}px;
        height: auto;
        display: block;
        margin: 0 auto;
        filter: brightness(0);
      }
      .num-comanda {
        text-align: center;
        font-size: 22px;
        font-weight: bold;
        margin: 3px 0;
        letter-spacing: 2px;
      }
      .hora {
        text-align: center;
        font-size: 11px;
        font-weight: bold;
        color: #000;
        margin: 2px 0;
      }
      .mesa-info {
        text-align: center;
        font-size: 16px;
        font-weight: bold;
        padding: 4px 0;
        color: #000;
        margin: 4px 0;
      }
      .cuenta-nombre {
        text-align: center;
        font-size: 12px;
        font-weight: bold;
        color: #000;
        font-style: italic;
        margin-bottom: 4px;
      }
      .linea {
        border-top: 2px solid #000;
        margin: 5px 0;
      }
      .linea-fina {
        border-top: 1px dashed #000;
        margin: 3px 0;
      }
      .fila {
        display: flex;
        justify-content: space-between;
        margin-bottom: 2px;
        font-size: 12px;
      }
      .fila-bold {
        display: flex;
        justify-content: space-between;
        font-weight: bold;
        font-size: 14px;
        margin-top: 3px;
      }
      .item-nombre {
        font-size: 12px;
        font-weight: bold;
      }
      .small {
        font-size: 10px;
        color: #000;
        font-weight: bold;
      }
      .datos-cliente {
        margin-top: 10px;
        font-size: 11px;
      }
      .datos-cliente .campo {
        display: flex;
        align-items: baseline;
        margin-bottom: 5px;
        gap: 4px;
      }
      .datos-cliente .campo label {
        white-space: nowrap;
        font-weight: bold;
        font-size: 11px;
      }
      .datos-cliente .campo .linea-input {
        flex: 1;
        border: none;
        border-bottom: 1px solid #000;
        min-width: 60px;
      }
      .pie {
        text-align: center;
        margin-top: 8px;
        font-size: 10px;
      }
    </style>
  `;

  const fecha = new Date();
  const fechaFormateada = `${fecha.getDate().toString().padStart(2, "0")}/${(
    fecha.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${fecha.getFullYear()}`;
  const horaFormateada = `${fecha
    .getHours()
    .toString()
    .padStart(2, "0")}:${fecha.getMinutes().toString().padStart(2, "0")}`;

  const subtotal = Number(cuenta?.Subtotal || 0);
  const propina = Math.floor(subtotal * 0.1);
  const total = subtotal + propina;

  const logoUrlCaja = window.location.origin + BRAND.logo;
  let html = `<div class='logo-comanda'><img src='${logoUrlCaja}' alt='${BRAND.nombre}'></div>`;
  html += `<div class='hotel'>${BRAND.nombre}</div>`;
  html += `<div class='titulo'>Cuenta del Restaurante</div>`;
  html += `<div class='hora'>${fechaFormateada} ${horaFormateada}</div>`;

  // Número de pedido — siempre visible y grande
  html += `<div class='num-comanda'>Pedido #${pedido?.IdPedido || "N/A"}</div>`;
  if (correlativo) {
    html += `<div class='subtitulo'>Comanda: ${correlativo}</div>`;
  }

  html += `<div class='mesa-info'>MESA ${mesa?.Numero || "N/A"}</div>`;

  if (cuenta?.NombreCuenta) {
    html += `<div class='cuenta-nombre'>Cuenta: ${cuenta.NombreCuenta}</div>`;
  }

  html += `<div class='linea'></div>`;

  // Items con precios
  detalles.forEach((det) => {
    const subtotalBase = Number(det.subtotal || 0);
    const extrasTotal = (det.opciones || []).reduce((sum, op) => {
      return sum + Number(op.precio_extra || 0) * Number(det.cantidad || 1);
    }, 0);
    const subtotalConExtras = subtotalBase + extrasTotal;
    html += `<div class='fila'>`;
    html += `<span class='item-nombre'>${det.nombre_platillo} <span style='font-weight:normal'>×${det.cantidad}</span></span>`;
    html += `<span>Q${subtotalConExtras.toFixed(2)}</span>`;
    html += `</div>`;
    if (det.opciones && det.opciones.length > 0) {
      det.opciones.forEach((op) => {
        const precioExtra = Number(op.precio_extra || 0);
        if (precioExtra > 0) {
          html += `<div style='padding-left:12px;font-size:11px;font-weight:bold;'>+ ${
            op.nombre_opcion || op.nombre_grupo || "Extra"
          }: Q${precioExtra.toFixed(2)}</div>`;
        }
      });
    }
  });

  html += `<div class='linea-fina'></div>`;
  html += `<div class='fila'><span>Subtotal</span><span>Q${subtotal.toFixed(
    2,
  )}</span></div>`;
  html += `<div class='fila'><span>Propina sugerida (10%)</span><span>Q${propina.toFixed(
    2,
  )}</span></div>`;
  html += `<div class='linea'></div>`;
  html += `<div class='fila-bold'><span>TOTAL</span><span>Q${total.toFixed(
    2,
  )}</span></div>`;

  if (mesero) {
    html += `<div class='small' style='text-align:center;margin-top:4px;'>Atendido por: ${mesero}</div>`;
  }

  // Campos para el cliente
  html += `<div class='datos-cliente'>`;
  html += `<div class='campo'><label>NIT:</label><span class='linea-input'>&nbsp;</span></div>`;
  html += `<div class='campo'><label>No. Habitación:</label><span class='linea-input'>&nbsp;</span></div>`;
  html += `<div class='campo'><label>Dirección:</label><span class='linea-input'>&nbsp;</span></div>`;
  html += `</div>`;

  html += `<div class='pie'>Gracias por su preferencia</div>`;
  html += `<div class='small' style='text-align:center;'>Pedido #${
    pedido?.IdPedido || "N/A"
  }</div>`;
  html += `<div class='linea-fina' style='margin-top:50px;'></div>`;
  html += `<div style='margin-top:20px;'>&nbsp;</div>`;

  win.document.write(
    `<html><head><title>Comanda Caja</title>${styles}</head><body>${html}</body></html>`,
  );
  win.document.close();

  const imgCaja = win.document.querySelector('img');
  if (imgCaja && !imgCaja.complete) {
    imgCaja.onload  = () => { win.focus(); win.print(); win.close(); };
    imgCaja.onerror = () => { win.focus(); win.print(); win.close(); };
  } else {
    setTimeout(() => { win.focus(); win.print(); win.close(); }, 300);
  }
}

export default {
  imprimirReciboRestaurante,
  imprimirReciboHotel,
  imprimirComandaCocina,
  imprimirComandaBarra,
  imprimirComandaCaja,
  imprimirReciboTienda,
};
